import React, { CSSProperties } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { ICropResult, ImageFormatType } from 'common/interfaces/uploadFile';

interface IImageCropperProps {
  src: string;
  aspectRatio?: number;
  imageStyle?: CSSProperties;
  onCrop: (result: ICropResult) => void;
}

interface ICropSettings {
  unit?: string;
  width?: number;
  height?: number;
  x?: number;
  y?: number;
  aspect?: number;
}

interface IImageCropperState {
  crop: ICropSettings;
  croppedImageUrl?: string;
  customCropAspect?: number;
}

export default class ImageCropper extends React.Component<IImageCropperProps, IImageCropperState> {
  private imageRef: HTMLImageElement;

  private maxSize = 1600;

  private croppedFileUrl: string;

  constructor(props: IImageCropperProps) {
    super(props);
    this.state = {
      crop: {
        unit: '%',
      },
    };
  }

  private onImageLoaded = (image: HTMLImageElement) => {
    this.imageRef = image;

    const { aspectRatio } = this.props;
    const imageWidth = image.width;
    const imageHeight = image.height;

    let width: number;
    let height: number;

    if (aspectRatio) {
      const originalAspect = imageWidth / imageHeight;

      if (originalAspect > aspectRatio) {
        height = imageHeight / 2;
        width = (imageHeight * aspectRatio) / 2;
      } else {
        width = imageWidth / 2;
        height = imageWidth / aspectRatio / 2;
      }
    } else {
      const size = Math.min(imageWidth, imageHeight) / 2;
      width = size;
      height = size;
    }

    const x = (imageWidth - width) / 2;
    const y = (imageHeight - height) / 2;

    const crop: ICropSettings = {
      unit: 'px',
      width,
      height,
      x,
      y,
      ...(aspectRatio ? { aspect: aspectRatio } : {}),
    };

    this.setState({ crop });
    this.onCropComplete(crop);

    return false;
  };

  private onCropComplete = async (crop: ICropSettings): Promise<void> => {
    const { onCrop } = this.props;
    if (this.imageRef && crop.width && crop.height) {
      const cropResult: ICropResult = await this.getCroppedImg(this.imageRef, crop);
      onCrop(cropResult);
    }
  };

  private onCropChange = (crop: ICropSettings) => {
    this.setState({ crop });
  };

  private getWidth = (value: number) => {
    if (value > this.maxSize) {
      return this.maxSize;
    }

    return value;
  };

  getCroppedImg(image: HTMLImageElement, crop: ICropSettings): Promise<ICropResult> {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    if (
      typeof crop.x === 'number' &&
      typeof crop.y === 'number' &&
      typeof crop.width === 'number' &&
      typeof crop.height === 'number'
    ) {
      const cropX = crop.x * scaleX;
      const cropY = crop.y * scaleY;
      const cropWidth = crop.width * scaleX;
      const cropHeight = crop.height * scaleY;

      canvas.width = cropWidth;
      canvas.height = cropHeight;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(image, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
      }

      return new Promise((resolve, reject) => {
        canvas.toBlob(blob => {
          if (!blob) {
            reject(new Error('No data for cropping'));
          } else {
            window.URL.revokeObjectURL(this.croppedFileUrl);
            const croppedFileUrl = canvas.toDataURL('image/jpeg', 1);
            this.croppedFileUrl = croppedFileUrl;
            resolve({ blob, url: croppedFileUrl });
          }
        }, ImageFormatType.JPEG);
      });
    } else {
      return Promise.reject(new Error('Crop dimensions are invalid'));
    }
  }

  render(): JSX.Element {
    const { src, imageStyle } = this.props;
    const { crop } = this.state;

    return (
      <div>
        {src && (
          <ReactCrop
            src={src}
            crop={crop}
            onImageLoaded={this.onImageLoaded}
            onComplete={this.onCropComplete}
            onChange={this.onCropChange}
            imageStyle={imageStyle}
          />
        )}
      </div>
    );
  }
}
